import React from 'react';
import { classNames } from '@/lib/utils';

interface CardProps {
    children: React.ReactNode;
    className?: string;
    clickable?: boolean;
    onClick?: () => void;
    padding?: 'sm' | 'md' | 'lg' | 'none';
}

const paddingStyles = {
    none: '',
    sm: 'p-3',
    md: 'p-4',
    lg: 'p-6',
};

export function Card({
    children,
    className,
    clickable = false,
    onClick,
    padding = 'md',
}: CardProps) {
    const baseClasses = 'bg-white rounded-lg border border-ui-border shadow-sm';
    const clickableClasses = clickable
        ? 'cursor-pointer transition-all duration-200 hover:shadow-md hover:border-brand-primary/30 focus:outline-none focus:ring-2 focus:ring-brand-primary/20 active:scale-[0.99] text-left w-full'
        : '';

    if (clickable) {
        return (
            <button
                className={classNames(baseClasses, paddingStyles[padding], clickableClasses, className)}
                onClick={onClick}
                type="button"
            >
                {children}
            </button>
        );
    }

    return (
        <div className={classNames(baseClasses, paddingStyles[padding], className)}>
            {children}
        </div>
    );
}

// Card Header
interface CardHeaderProps {
    children: React.ReactNode;
    className?: string;
}

export function CardHeader({ children, className }: CardHeaderProps) {
    return (
        <div className={classNames('mb-3', className)}>
            {children}
        </div>
    );
}

// Card Title
interface CardTitleProps {
    children: React.ReactNode;
    className?: string;
}

export function CardTitle({ children, className }: CardTitleProps) {
    return (
        <h3 className={classNames('text-lg font-semibold text-content-title', className)}>
            {children}
        </h3>
    );
}

// Card Description
interface CardDescriptionProps {
    children: React.ReactNode;
    className?: string;
}

export function CardDescription({ children, className }: CardDescriptionProps) {
    return (
        <p className={classNames('text-sm text-content-muted', className)}>
            {children}
        </p>
    );
}

// Card Content
interface CardContentProps {
    children: React.ReactNode;
    className?: string;
}

export function CardContent({ children, className }: CardContentProps) {
    return (
        <div className={classNames('', className)}>
            {children}
        </div>
    );
}

// Card Footer
interface CardFooterProps {
    children: React.ReactNode;
    className?: string;
}

export function CardFooter({ children, className }: CardFooterProps) {
    return (
        <div className={classNames('mt-4 pt-4 border-t border-ui-border', className)}>
            {children}
        </div>
    );
}

export default Card;
